import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import InputAdornment from "@mui/material/InputAdornment";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import React, { useState, useEffect, useCallback, useRef } from "react";

// ─── Helpers ──────────────────────────────────────────────────
const round2 = (v) => Math.round((Number(v) || 0) * 100) / 100;

const fmt = (v) =>
  new Intl.NumberFormat("th-TH", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(round2(v));

// ดึงมูลค่ารวมต่อ row (รองรับ 2 รูปแบบ field name)
const getRowTotal = (r) => {
  const t = Number(r.total_amount ?? r.netValue ?? 0);
  if (t > 0) return t;
  // fallback: price_per_unit * quantity
  const p = Number(r.price_per_unit ?? r.excludeVat ?? 0);
  const q = Number(r.quantity ?? 1);
  return p * q;
};

const getRowTaxable = (r) => {
  const t = Number(r.taxable_amount ?? r.excludeVat ?? 0);
  if (t > 0) return t;
  const p = Number(r.price_per_unit ?? 0);
  const q = Number(r.quantity ?? 1);
  return p * q;
};

const getRowVat = (r) => Number(r.vat ?? 0);

// ─── Component ────────────────────────────────────────────────
export default function InformationSummary({
  values,
  setFieldValue,
  handleChange,
  errors,
  formdisabled,
  productRow = [],
}) {
  const [expand, setExpand] = useState(true);

  // ── Discount local input state ────────────────────────────────
  const [discountRateInput, setDiscountRateInput] = useState(""); // สำหรับ %
  const [discountAmountInput, setDiscountAmountInput] = useState(""); // สำหรับ บาท
  const isTypeChange = useRef(false);

  // sync local state เมื่อ values เปลี่ยนจากภายนอก (เช่นโหลดข้อมูล)
  useEffect(() => {
    if (isTypeChange.current) {
      isTypeChange.current = false;
      return;
    }
    if (values.discount_type === "percent") {
      setDiscountRateInput(
        values.discount_rate != null && values.discount_rate !== ""
          ? String(values.discount_rate)
          : "",
      );
    } else {
      setDiscountAmountInput(
        values.discount_rate != null && values.discount_rate !== ""
          ? parseFloat(values.discount_rate).toFixed(2)
          : "",
      );
    }
  }, [values.discount_type, values.discount_rate]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleDiscountInputChange = useCallback(
    (e) => {
      let raw = e.target.value;
      if (values.discount_type === "percent") {
        // % — digits only, clamp 0-100
        raw = raw.replace(/[^\d]/g, "");
        const n = parseFloat(raw);
        if (!isNaN(n)) {
          const clamped = Math.min(Math.max(n, 0), 100);
          setDiscountRateInput(String(clamped));
          setFieldValue("discount_rate", clamped);
        } else {
          setDiscountRateInput("");
          setFieldValue("discount_rate", null);
        }
      } else {
        // บาท — digits + dot, max 2 ทศนิยม
        raw = raw.replace(/[^\d.]/g, "");
        const parts = raw.split(".");
        if (parts.length > 2) raw = parts[0] + "." + parts.slice(1).join("");
        if (parts.length === 2) raw = parts[0] + "." + parts[1].slice(0, 2);
        setDiscountAmountInput(raw);
      }
    },
    [values.discount_type, setFieldValue],
  );

  const handleDiscountBlur = useCallback(() => {
    if (values.discount_type === "amount") {
      const n = parseFloat(discountAmountInput);
      const max = round2(values.amount_before_discount || 0);
      if (!isNaN(n)) {
        const clamped = Math.min(n, max);
        setDiscountAmountInput(clamped.toFixed(2));
        setFieldValue("discount_rate", clamped);
      } else {
        setDiscountAmountInput("");
        setFieldValue("discount_rate", null);
      }
    }
  }, [
    values.discount_type,
    values.amount_before_discount,
    discountAmountInput,
    setFieldValue,
  ]);

  const handleDiscountKeyDown = useCallback(
    (e) => {
      const allowedKeys = [
        "Backspace",
        "Delete",
        "Tab",
        "Escape",
        "Enter",
        "ArrowLeft",
        "ArrowRight",
        "ArrowUp",
        "ArrowDown",
        "Home",
        "End",
      ];
      const isDigit = /^[0-9]$/.test(e.key);
      const isDot = e.key === ".";
      const isAllowed = allowedKeys.includes(e.key);
      const isCtrl =
        e.ctrlKey && ["a", "c", "v", "x"].includes(e.key.toLowerCase());

      // % ไม่อนุญาตทศนิยม
      if (values.discount_type === "percent" && isDot) {
        e.preventDefault();
        return;
      }

      if (!isDigit && !isDot && !isAllowed && !isCtrl) {
        e.preventDefault();
        return;
      }

      // ห้าม dot ซ้ำ หรือขึ้นต้นด้วย dot
      if (isDot && (e.target.value.includes(".") || e.target.value === "")) {
        e.preventDefault();
        return;
      }

      // บาท — จำกัด 2 ทศนิยม
      if (values.discount_type === "amount" && isDigit) {
        const val = e.target.value;
        const dotIdx = val.indexOf(".");
        if (dotIdx !== -1) {
          const decPart = val.substring(dotIdx + 1);
          const cursor = e.target.selectionStart;
          if (cursor > dotIdx && decPart.length >= 2) {
            e.preventDefault();
          }
        }
      }
    },
    [values.discount_type],
  );

  const handleDiscountTypeChange = useCallback(
    (e) => {
      const newType = e.target.value;
      isTypeChange.current = true;
      setFieldValue("discount_type", newType);
      setFieldValue("discount_rate", null);
      setDiscountRateInput("");
      setDiscountAmountInput("");
    },
    [setFieldValue],
  );

  // ── Tag (chip) helpers ────────────────────────────────────────
  const isDuplicateTag = useCallback((newTag, existingTags) => {
    return existingTags.some((t) => t.toLowerCase() === newTag.toLowerCase());
  }, []);

  const handleTagsChange = (_, newValue) => {
    const uniqueTags = [];
    newValue.forEach((t) => {
      const tag = typeof t === "string" ? t : t.label || t;
      if (!isDuplicateTag(tag, uniqueTags)) uniqueTags.push(tag);
    });
    setFieldValue("tag", uniqueTags);
  };

  // ── Recalculate summary whenever productRow or discount changes ──
  useEffect(() => {
    const validRows = productRow.filter(
      (r) => getRowTotal(r) > 0 || getRowTaxable(r) > 0,
    );

    const sumTotal = validRows.reduce((s, r) => s + getRowTotal(r), 0);
    const sumTaxable = validRows.reduce((s, r) => s + getRowTaxable(r), 0);
    const sumVat = validRows.reduce((s, r) => s + getRowVat(r), 0);

    // amount_before_discount = ผลรวม total ของทุก row (หลัง row-level discount แล้ว)
    const amountBeforeDiscount = round2(sumTotal || sumTaxable + sumVat);

    // Document-level discount
    const discountRate = Number(values.discount_rate) || 0;
    let discountAmount = 0;
    if (values.discount_type === "percent") {
      discountAmount = round2((amountBeforeDiscount * discountRate) / 100);
    } else {
      discountAmount = round2(Math.min(discountRate, amountBeforeDiscount));
    }
    const amountAfterDiscount = round2(amountBeforeDiscount - discountAmount);

    // แยกตาม VAT ของแต่ละ row
    const taxableExempt = round2(
      validRows
        .filter((r) => getRowVat(r) === 0)
        .reduce((s, r) => s + getRowTaxable(r), 0),
    );
    const taxableHasVat = round2(
      validRows
        .filter((r) => getRowVat(r) > 0)
        .reduce((s, r) => s + getRowTaxable(r), 0),
    );
    const vatAmount = round2(sumVat);
    const grandTotal = amountAfterDiscount;

    setFieldValue("amount_before_discount", amountBeforeDiscount);
    setFieldValue("discount_amount", discountAmount);
    setFieldValue("amount_after_discount", amountAfterDiscount);
    setFieldValue("taxable_amount_exempt", taxableExempt);
    setFieldValue("taxable_amount_zero_vat", 0);
    setFieldValue("taxable_amount_has_vat", taxableHasVat);
    setFieldValue("vat_amount", vatAmount);
    setFieldValue("grand_total", grandTotal);
  }, [productRow, values.discount_type, values.discount_rate]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="mb-3 mt-0 shadow bg-white rounded-xl md:p-6 p-4 overflow-hidden">
      <div className="flex items-start">
        <div className="grow">
          <div className="text-base font-semibold text-[#242F48]">
            สรุปข้อมูล
          </div>
        </div>

        <div className="text-sm text-[#636578]">
          <div className="flex">
            {/* ── ตารางซ้าย: ก่อน/หลังส่วนลด ── */}
            <div className="w-[420px]">
              <table className="table-s1">
                <tbody>
                  <tr>
                    <td className="text-right">มูลค่าสินค้าก่อนส่วนลด</td>
                    <td className="font-bold text-right" width="38%">
                      {fmt(values.amount_before_discount)}
                    </td>
                    <td width="10%">บาท</td>
                  </tr>

                  {/* แถวส่วนลด */}
                  <tr>
                    <td className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span>ส่วนลด</span>
                        {/* Combined input + type selector — MUI style (same as pardesco) */}
                        <TextField
                          type="text"
                          variant="outlined"
                          size="small"
                          inputProps={{ inputMode: "decimal" }}
                          value={
                            values.discount_type === "percent"
                              ? discountRateInput
                              : discountAmountInput
                          }
                          disabled={formdisabled}
                          onChange={handleDiscountInputChange}
                          onBlur={handleDiscountBlur}
                          onKeyDown={handleDiscountKeyDown}
                          autoComplete="off"
                          placeholder="0.00"
                          sx={{
                            width: 160,
                            "& .MuiInputBase-root.MuiOutlinedInput-root": {
                              pr: 0,
                            },
                          }}
                          slotProps={{
                            input: {
                              endAdornment: (
                                <InputAdornment position="end">
                                  <FormControl
                                    sx={{
                                      width: "65px",
                                      border: 0,
                                      textAlign: "left",
                                      paddingLeft: "2px",
                                      ".MuiSelect-select": {
                                        fontSize: "12px !important",
                                      },
                                    }}
                                  >
                                    <Select
                                      sx={{
                                        border: 0,
                                        paddingLeft: 0,
                                        background: "#F1F3F6",
                                        fieldset: {
                                          border: 0,
                                          paddingLeft: 0,
                                          borderWidth: "0 !important",
                                        },
                                      }}
                                      size="small"
                                      value={values.discount_type}
                                      onChange={handleDiscountTypeChange}
                                      disabled={formdisabled}
                                    >
                                      <MenuItem value="percent">%</MenuItem>
                                      <MenuItem value="amount">บาท</MenuItem>
                                    </Select>
                                  </FormControl>
                                </InputAdornment>
                              ),
                            },
                          }}
                        />
                      </div>
                    </td>
                    <td className="font-bold text-right">
                      {fmt(values.discount_amount)}
                    </td>
                    <td>บาท</td>
                  </tr>

                  <tr>
                    <td className="text-right">มูลค่าสินค้าหลังหักส่วนลด</td>
                    <td className="font-bold text-right">
                      {fmt(values.amount_after_discount)}
                    </td>
                    <td>บาท</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ── ตารางกลาง: ภาษี ── */}
            <div className="w-[380px]">
              <table className="table-s1">
                <tbody>
                  <tr>
                    <td className="text-right">มูลค่าสินค้ายกเว้นภาษี</td>
                    <td className="font-bold text-right" width="40%">
                      {fmt(values.taxable_amount_exempt)}
                    </td>
                    <td width="10%">บาท</td>
                  </tr>
                  <tr>
                    <td className="text-right">มูลค่าสินค้าภาษี 0 %</td>
                    <td className="font-bold text-right">
                      {fmt(values.taxable_amount_zero_vat)}
                    </td>
                    <td>บาท</td>
                  </tr>
                  <tr>
                    <td className="text-right">มูลค่าสินค้าภาษี 7 %</td>
                    <td className="font-bold text-right">
                      {fmt(values.taxable_amount_has_vat)}
                    </td>
                    <td>บาท</td>
                  </tr>
                  <tr>
                    <td className="text-right">ภาษีมูลค่าเพิ่ม</td>
                    <td className="font-bold text-right">
                      {fmt(values.vat_amount)}
                    </td>
                    <td>บาท</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* ── ยอดสุทธิ ── */}
            <div className="w-[200px] ml-10">
              <div className="mb-2 mt-1">มูลค่าสุทธิรวม</div>
              <div className="bg-[#2DC76D] text-white rounded-lg px-2 py-3 text-center">
                <span className="font-bold text-[24px]">
                  {fmt(values.grand_total)}
                </span>
                <span className="ml-1">บาท</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── หมายเหตุ ── */}
      {expand && (
        <>
          <hr className="!my-4" />
          <div className="flex gap-4 mr-72">
            <div className="flex-1">
              <div className="text-base font-semibold text-[#242F48] mb-2">
                หมายเหตุ
              </div>
              <TextField
                fullWidth
                label=""
                placeholder="หมายเหตุ"
                multiline
                name="remark"
                value={values.remark ?? ""}
                error={!!errors.remark}
                onChange={handleChange}
                disabled={formdisabled}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "130px",
                    alignItems: "flex-start",
                    "& textarea": {
                      height: "100% !important",
                      overflowY: "auto !important",
                    },
                  },
                }}
              />
            </div>
            <div className="flex-1">
              <div className="text-base font-semibold text-[#242F48] mb-2">
                แท็ก
              </div>
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={values.tag ?? []}
                onChange={handleTagsChange}
                disabled={formdisabled}
                renderTags={(tagValues, getTagProps) =>
                  tagValues.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index });
                    return (
                      <Chip
                        key={key}
                        variant="outlined"
                        label={option}
                        size="small"
                        {...tagProps}
                        sx={{
                          borderColor: "#565DFF",
                          color: "#565DFF",
                          "& .MuiChip-deleteIcon": {
                            color: "#FF0000",
                            "&:hover": { color: "#cc0000" },
                          },
                        }}
                      />
                    );
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root": {
                    height: "130px",
                    alignItems: "flex-start",
                    padding: "12px",
                    borderRadius: "4px",
                    flexWrap: "wrap",
                    overflowY: "auto",
                    gap: "6px",
                  },
                  "& .MuiAutocomplete-input": {
                    padding: "0 !important",
                    minWidth: "120px",
                  },
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      (values.tag ?? []).length === 0 ? "พิมพ์แล้วกด Enter" : ""
                    }
                    disabled={formdisabled}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && e.target.value.trim()) {
                        e.preventDefault();
                        const newTag = e.target.value.trim();
                        const current = values.tag ?? [];
                        if (!isDuplicateTag(newTag, current)) {
                          setFieldValue("tag", [...current, newTag]);
                        }
                        e.target.value = "";
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>
        </>
      )}

      <div className="text-right mb-0 mt-2">
        <Button
          variant="text"
          color="primary"
          className="ml-auto !text-base"
          onClick={() => setExpand((p) => !p)}
        >
          ย่อ/ขยาย
        </Button>
      </div>
    </div>
  );
}
